import { MODELS } from '../data/models';
import { SYNTH_MODES } from '../data/synth-modes';
import { state, savedState } from './state';
import { t, getCurrentLang } from './i18n';
import { escapeHtml, renderMarkdown, copyToClipboard } from './ui-helpers';
import { toast } from './toast';
import { addSpent, calcCost } from './cost-tracking';
import { callModel } from '../api';
import { getConstellation } from './constellation';
import { mountExportMenu, buildSynthMarkdown } from './exports';
import { appendSynthesis } from './history-client';
import { triggerAutosave } from './autosave';
import { extractCodeBlocks } from './code-block-extractor';

/* =================================================================
   11. Synthesis (consolidate)
   ================================================================= */
export function attachSynthesisHandlers() {
  document.getElementById("synth-btn").addEventListener("click", async () => {
    const ok = state.lastResponses.filter(r => r.ok);
    if (ok.length < 2) { toast(t("err.nomodels")); return; }

    const modeKey = document.getElementById("synth-mode").value;
    const mode = SYNTH_MODES[modeKey];
    let instruction = mode.instruction[getCurrentLang()] || mode.instruction.en;
    if (modeKey === "custom") {
      instruction = document.getElementById("synth-custom-input").value.trim();
      if (!instruction) { toast(t("err.nocustom")); return; }
    }

    const synthModelId = document.getElementById("synth-model").value;
    const synthModel = MODELS.find(m => m.id === synthModelId);
    if (!synthModel) return;

    const out = document.getElementById("synth-output");
    out.classList.remove("empty");
    out.innerHTML = `
      <div class="skeleton" style="padding: 0;">
        <div class="skeleton-line"></div>
        <div class="skeleton-line"></div>
        <div class="skeleton-line"></div>
        <div class="skeleton-line"></div>
      </div>
    `;

    const responsesBlock = ok.map((r, i) => {
      const m = MODELS.find(x => x.id === r.modelId);
      return `--- Model ${i+1} (${m ? m.label : r.modelId}) ---\n${r.text}\n`;
    }).join("\n");

    const synthPrompt = t("synth.prompt.wrap", {
      n: ok.length,
      prompt: state.lastPrompt,
      responses: responsesBlock,
      instruction,
    });

    const btn = document.getElementById("synth-btn");
    btn.disabled = true;

    // Trigger 3D synthesis animation: collapse selected nodes to the iris
    const constellation = getConstellation();
    if (constellation) constellation.triggerSynthesize(synthModelId);

    try {
      // Request the FULL model ceiling for synthesis (the call most likely
      // to need long output). synthModel.maxOut comes from MODELS in
      // src/data/models.ts; if missing, callModel falls back to 8192.
      const r = await callModel(synthModel, synthPrompt, {
        maxOutTokens: synthModel.maxOut,
      });
      const text = r.text;
      addSpent(synthModelId, calcCost(synthModelId, r.inTokens, r.outTokens));
      // Heuristic truncation: the adapter sets truncated=true only when the
      // provider explicitly reports MAX_TOKENS / length. Some providers
      // return a "normal" stop reason even when they hit the cap (or stop
      // close to it), so also treat "outTokens >= 90% of the requested
      // cap" as a likely truncation. Catches the silent-cut case.
      const cap = synthModel.maxOut || 8192;
      const nearCap = typeof r.outTokens === 'number' && r.outTokens >= Math.floor(cap * 0.9);
      const isTruncated = !!r.truncated || nearCap;
      renderSynthOutput(out, text, isTruncated, synthModel, modeKey);
      savedState.lastSynthText = text;
      savedState.lastSynthMode = modeKey;
      savedState.lastSynthModelId = synthModelId;
      savedState.lastSynthTruncated = isTruncated;
      // Push to the per-session syntheses history so saved snapshots
      // can replay multi-step consolidations faithfully.
      state.syntheses.push({
        mode: modeKey,
        model_id: synthModelId,
        text,
        truncated: isTruncated,
        is_continuation: false,
        created_at: new Date().toISOString(),
      });
      // Harvest fenced code blocks from the synth output too.
      extractCodeBlocks(text, 'synth');
      document.getElementById("synth-actions").style.display = "";
      // Persist to history (best-effort) — appends to the same interaction
      // the runner created, so /history.html shows synthesis alongside the
      // original responses.
      const lastIID = (state as any).lastInteractionId;
      if (lastIID) {
        appendSynthesis(lastIID, {
          mode: modeKey,
          model_id: synthModelId,
          text,
        }).catch(() => { /* already logged by history-client */ });
      }
      // Reveal optimal-prompt panel for next step
      document.getElementById("optimal-panel").style.display = "";
      document.getElementById("optimal-output").value = "";
      document.getElementById("optimal-actions").style.display = "none";
      savedState.lastOptimalPrompt = "";
      // Trigger autosave (no-op unless enabled in settings).
      triggerAutosave();
    } catch (err) {
      out.innerHTML = `<div class="response-error" style="border:none; background:rgba(243,130,129,0.06); border-radius:8px;">${escapeHtml(err.message || String(err))}</div>`;
    } finally {
      btn.disabled = false;
    }
  });

  /* =================================================================
     11b. Synth save (multi-format) + copy
     ================================================================= */
  mountExportMenu(
    document.getElementById("synth-save"),
    ["md", "docx", "pdf", "pptx-detailed", "pptx-summary", "pptx-executive"],
    () => {
      if (!savedState.lastSynthText) return null;
      const md = buildSynthMarkdown(savedState.lastSynthText, savedState.lastSynthMode, savedState.lastSynthModelId);
      const synthModel = MODELS.find(m => m.id === savedState.lastSynthModelId);
      const modeLabel = SYNTH_MODES[savedState.lastSynthMode]?.labels[getCurrentLang()] || savedState.lastSynthMode;
      const sources = state.lastResponses.filter(r => r.ok).map(r => {
        const m = MODELS.find(x => x.id === r.modelId);
        return m ? m.label : r.modelId;
      });
      const sections = [
        { heading: t("export.section.prompt"), markdown: state.lastPrompt },
        {
          heading: t("export.section.synthesis"),
          subhead: `${modeLabel} · ${synthModel ? synthModel.label : ""} · ${t("export.section.sources")}: ${sources.join(", ")}`,
          markdown: savedState.lastSynthText,
        },
      ];
      return { title: "Lorenza · Synthesis", markdown: md, sections };
    },
    "lorenza-synthesis"
  );

  document.getElementById("synth-copy").addEventListener("click", async () => {
    if (!savedState.lastSynthText) { toast(t("save.nothing")); return; }
    const ok = await copyToClipboard(savedState.lastSynthText);
    toast(ok ? t("copy.done") : t("copy.failed"));
  });
}

/* =================================================================
   11c. Render synth output + truncation banner + Continue button
   ================================================================= */

function renderSynthOutput(out: HTMLElement, text: string, truncated: boolean, synthModel: any, modeKey: string) {
  const md = renderMarkdown(text) || `<em>(empty)</em>`;
  // Auto-detected truncation gets a loud yellow banner. Otherwise we still
  // surface a soft "Continue →" footer so the user can extend manually
  // when their reading tells them the model stopped early but didn't
  // signal MAX_TOKENS. Eliminates the silent-cut false negative.
  const banner = truncated ? `
    <div class="synth-truncated" role="status" style="
      margin: 0 0 14px;
      padding: 10px 14px;
      background: rgba(252,226,138,0.10);
      border: 1px solid rgba(252,226,138,0.34);
      border-radius: 8px;
      font-size: 13px;
      color: var(--on-dark-2);
      display: flex; gap: 14px; align-items: center; justify-content: space-between;
    ">
      <span><strong>⚠ Truncated.</strong> The model hit its output cap. Click <em>Continue</em> to extend from where it stopped.</span>
      <button id="synth-continue-btn" class="header-action-btn" style="padding: 6px 14px; font-size: 12px; white-space: nowrap;">
        Continue →
      </button>
    </div>
  ` : "";
  const footer = !truncated ? `
    <div class="synth-continue-footer" style="
      margin: 18px 0 0;
      padding-top: 12px;
      border-top: 1px solid var(--border);
      display: flex; justify-content: flex-end; gap: 10px;
      font-size: 12px; color: var(--on-dark-3);
    ">
      <span style="align-self: center;">Need more? Models sometimes stop early.</span>
      <button id="synth-continue-soft-btn" class="header-action-btn" style="padding: 6px 14px; font-size: 12px; white-space: nowrap;">
        Continue →
      </button>
    </div>
  ` : "";
  out.innerHTML = banner + md + footer;
  document.getElementById("synth-continue-btn")?.addEventListener("click", () =>
    continueSynthesis(out, synthModel, modeKey)
  );
  document.getElementById("synth-continue-soft-btn")?.addEventListener("click", () =>
    continueSynthesis(out, synthModel, modeKey)
  );
}

async function continueSynthesis(out: HTMLElement, synthModel: any, modeKey: string) {
  const prior = savedState.lastSynthText || "";
  if (!prior) return;
  // Continuation prompt — give the model the original prompt context + what
  // it produced so far, and ask it to keep going from the exact cut point.
  const tail = prior.length > 8000 ? prior.slice(-8000) : prior;
  const contPrompt = [
    "You previously wrote the following synthesis for the user's prompt,",
    "but the response was cut off because you ran out of output tokens.",
    "Continue from EXACTLY where you stopped — no preamble, no recap, no",
    "summary of what you already said. Pick up the next sentence/section",
    "and finish the document.",
    "",
    "Original user prompt:",
    state.lastPrompt || "",
    "",
    "Your previous output (tail shown for context, complete continuation needed):",
    "---",
    tail,
    "---",
    "Continue:",
  ].join("\n");

  // Either button may be present depending on whether the first call
  // surfaced as auto-detected or soft-suggested.
  const hardBtn = document.getElementById("synth-continue-btn") as HTMLButtonElement | null;
  const softBtn = document.getElementById("synth-continue-soft-btn") as HTMLButtonElement | null;
  [hardBtn, softBtn].forEach(b => { if (b) { b.disabled = true; b.textContent = "Continuing…"; } });
  try {
    const r = await callModel(synthModel, contPrompt, { maxOutTokens: synthModel.maxOut });
    addSpent(synthModel.id, calcCost(synthModel.id, r.inTokens, r.outTokens));
    const merged = prior + (r.text || "");
    savedState.lastSynthText = merged;
    // Apply the same heuristic for the continuation call so if it also hits
    // the cap we keep the loud banner.
    const cap = synthModel.maxOut || 8192;
    const nearCap = typeof r.outTokens === 'number' && r.outTokens >= Math.floor(cap * 0.9);
    const isTruncated = !!r.truncated || nearCap;
    savedState.lastSynthTruncated = isTruncated;
    // Also push the continuation chunk as its own row in the per-session
    // syntheses history. Saved snapshots can replay merged text correctly.
    state.syntheses.push({
      mode: modeKey + ":continuation",
      model_id: synthModel.id,
      text: r.text || "",
      truncated: isTruncated,
      is_continuation: true,
      created_at: new Date().toISOString(),
    });
    extractCodeBlocks(r.text || "", 'synth-continuation');
    renderSynthOutput(out, merged, isTruncated, synthModel, modeKey);
    // Append the continuation as its own synthesis row in history so we keep
    // an audit trail of what was added in the second call.
    const lastIID = (state as any).lastInteractionId;
    if (lastIID) {
      appendSynthesis(lastIID, {
        mode: modeKey + ":continuation",
        model_id: synthModel.id,
        text: r.text || "",
      }).catch(() => {});
    }
    triggerAutosave();
  } catch (err: any) {
    toast(err?.message || "Continue failed");
    [hardBtn, softBtn].forEach(b => { if (b) { b.disabled = false; b.textContent = "Continue →"; } });
  }
}
